import { createSlice } from "@reduxjs/toolkit";
import type { RootState } from "../store/store";
import { AppState, IAppNotificationPayload, ILocalNotification, ISidebarCounterMap } from "./app.interfaces";
import { defaultAppConfig } from "./defaultAppConfig";

const { clubNotifications, maxClubbedNotifications, suppressOnSessionExpiry } = defaultAppConfig.notification;

const EMPTY_NOTIFICATION: IAppNotificationPayload = {
  title: "",
  msg: undefined,
  type: "white",
  notificationType: "modal",
};

/** NotificationModal falls back to `danger` when a payload omits `type`, so severity resolves the same way here. */
const resolveNotificationType = (notification: IAppNotificationPayload) => notification.type ?? "danger";

const isErrorNotification = (notification: IAppNotificationPayload) => resolveNotificationType(notification) === "danger";

/** Once the token has expired the only useful action left is logging back in - see defaultAppConfig.notification. */
const isSuppressedBySessionExpiry = (notification: IAppNotificationPayload) => {
  if (suppressOnSessionExpiry === "none") return false;
  if (suppressOnSessionExpiry === "all") return true;
  return isErrorNotification(notification);
};

const isSameNotification = (a: IAppNotificationPayload, b: IAppNotificationPayload) =>
  a.title === b.title && a.msg === b.msg;

const INITIAL_STATE: AppState = {
  appNotification: { ...EMPTY_NOTIFICATION },
  modalNotifications: [],
  showSessionExpiredModal: false,

  localNotification: [],
  sidebarCounter: undefined,
};
const appSlice = createSlice({
  name: "app",
  initialState: INITIAL_STATE,
  reducers: {
    appNotification: (state: AppState, { payload }: { payload: IAppNotificationPayload }) => {
      // Every notification renders as a modal unless the caller explicitly asks for another surface.
      const notification = { ...payload, notificationType: payload.notificationType ?? "modal" };

      // A payload with no message is the "clear the transient surface" dispatch rather than a new
      // notification, so it must not disturb whatever the modal is currently showing.
      if (!notification.msg) {
        state.appNotification = notification;
        return;
      }

      if (state.showSessionExpiredModal && isSuppressedBySessionExpiry(notification)) return;

      state.appNotification = notification;

      if (notification.notificationType !== "modal") return;

      const queue = state.modalNotifications;
      const isSameSeverity =
        queue.length > 0 && resolveNotificationType(queue[0]) === resolveNotificationType(notification);

      // A success landing mid-error-burst replaces the group rather than joining it -
      // "Saved successfully" listed under three failures reads as a bug.
      if (!clubNotifications || !isSameSeverity) {
        state.modalNotifications = [notification];
        return;
      }
      // Parallel calls failing the same way produce the same message, so show it once.
      if (queue.some((queued) => isSameNotification(queued, notification))) return;
      if (queue.length >= maxClubbedNotifications) return;
      queue.push(notification);
    },
    dismissAppNotification: (state: AppState) => {
      state.modalNotifications = [];
      state.appNotification = { ...EMPTY_NOTIFICATION };
    },
    localNotification: (state: AppState, { payload }: { payload: ILocalNotification }) => {
      state.localNotification = [{ ...payload }, ...state.localNotification];
    },
    updateSidebarCounter: (state: AppState, { payload }: { payload: ISidebarCounterMap }) => {
      state.sidebarCounter = { ...payload };
    },
    showSessionExpiredModal: (state) => {
      state.showSessionExpiredModal = true;
      // Errors already queued when the 401 landed would otherwise stack on top of this modal.
      state.modalNotifications = state.modalNotifications.filter(
        (notification) => !isSuppressedBySessionExpiry(notification)
      );
      if (isSuppressedBySessionExpiry(state.appNotification)) state.appNotification = { ...EMPTY_NOTIFICATION };
    },
    hideSessionExpiredModal: (state) => {
      state.showSessionExpiredModal = false;
    },
  },
});

export const {
  appNotification,
  dismissAppNotification,
  localNotification,
  updateSidebarCounter,
  showSessionExpiredModal,
  hideSessionExpiredModal,
} = appSlice.actions;
export const selectAppNotification = (state: RootState) => state.app.appNotification;
export const selectModalNotifications = (state: RootState) => state.app.modalNotifications;
export const selectSidebarCounter = (state: RootState) => state.app.sidebarCounter;
export const selectLocalNotification = (state: RootState) => state.app.localNotification;
export const unReadLocalNotificationCount = (state: RootState) =>
  state.app.localNotification.map((v) => !v.isRead);
export const selectShowSessionExpiredModal = (state: RootState) => state.app.showSessionExpiredModal;

export default appSlice.reducer;
